import axios from 'axios';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://store.comparehomeupgrades.com/api';
const LEVELS = { none: 0, view: 1, edit: 2 };

export const MODULE_LABELS = {
  summary: 'Summary page (orders overview)',
  shopify: 'Shopify',
  amazon: 'Amazon',
  ebay: 'eBay',
  sku_mapping: 'SKU Mapping & stock imports',
  ledger: 'Ledger',
  settings: 'Settings',
  reports: 'Monthly Reports',
  cogs: 'COGS Rates',
  stock_sync: 'Stock Sync',
};
export const MODULES = Object.keys(MODULE_LABELS);
// The sidebar pages, shown under "Summary page" when setting up an account. Each has its own access.
export const SIDEBAR_PAGES = [
  { key: 'summary', label: 'Dashboard', levels: [['none', 'No access'], ['view', 'View only'], ['edit', 'View & edit']] },
  { key: 'settings', label: 'Settings', levels: [['none', 'No access'], ['edit', 'Change own password']] },
  { key: 'reports', label: 'Monthly Reports', levels: [['none', 'No access'], ['view', 'Download'], ['edit', 'Download + fetch Amazon data']] },
  { key: 'cogs', label: 'COGS Rates', levels: [['none', 'No access'], ['view', 'View only'], ['edit', 'View & edit']] },
  { key: 'stock_sync', label: 'Stock Sync', levels: [['none', 'No access'], ['view', 'View only'], ['edit', 'View & edit']] },
];
export const PLATFORM_MODULES = ['shopify', 'amazon', 'ebay', 'sku_mapping', 'ledger'];

export function getUser() {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
}
export function setSession(token, user) {
  if (token) localStorage.setItem('token', token);
  if (user) localStorage.setItem('user', JSON.stringify(user));
}
export function clearSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
}
export function isAdmin(user = getUser()) {
  return user?.role === 'admin';
}
// can('ebay') = at least view; can('ebay', 'edit') = allowed to change things there
export function can(module, level = 'view', user = getUser()) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return (LEVELS[user.permissions?.[module]] || 0) >= LEVELS[level];
}

let lastDeniedToast = 0;
function onAuthFailure(status, message) {
  if (status === 401) {
    clearSession();
    window.location.href = '/';
  } else if (status === 403 && Date.now() - lastDeniedToast > 3000) {
    lastDeniedToast = Date.now();
    toast.warn(message || 'Your account does not have permission for this');
  }
}

const isApiUrl = (url) => typeof url === 'string' && url.startsWith(API_BASE_URL) && !url.endsWith('/auth/login');

// Every component talks to the API with its own fetch/axios calls; rather than editing each one,
// attach the login token to every API request in one place and handle "not logged in" / "no
// permission" answers consistently.
export function installAuthInterceptors() {
  if (window.__authInterceptorsInstalled) return;
  window.__authInterceptorsInstalled = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url;
    if (!isApiUrl(url)) return originalFetch(input, init);
    const token = localStorage.getItem('token');
    const headers = new Headers(init.headers || (typeof input !== 'string' ? input.headers : undefined) || {});
    if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
    const res = await originalFetch(input, { ...init, headers });
    if (res.status === 401 || res.status === 403) {
      let message;
      try { message = (await res.clone().json())?.error; } catch { /* not JSON */ }
      onAuthFailure(res.status, message);
    }
    return res;
  };

  axios.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    const url = (config.baseURL || '') + (config.url || '');
    if (token && isApiUrl(url.startsWith('http') ? url : API_BASE_URL + url)) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
  axios.interceptors.response.use(null, (error) => {
    const status = error.response?.status;
    const url = (error.config?.baseURL || '') + (error.config?.url || '');
    if ((status === 401 || status === 403) && isApiUrl(url.startsWith('http') ? url : API_BASE_URL + url)) {
      onAuthFailure(status, error.response?.data?.error);
    }
    return Promise.reject(error);
  });
}
